/**
 * Phones/tablets (<1068px): tuck the header's search row away while scrolling down, bring it
 * back on any scroll up and always at the top. Desktop keeps search inline in the header row,
 * so nothing happens there.
 *
 * Why: the search row is 58px of a sticky ~126px header, on every screen of every page
 * (2026-10-11: "it takes too much space").
 *
 * No page jump: collapsing a row inside a sticky header shrinks its in-flow height and would
 * pull the whole page up 58px. bh-header-search.css adds the same 58px as margin-bottom on
 * the section, on the same transition, so the document never moves; the header just gets
 * shorter and shows more page under it.
 *
 * Never tucks while the search is in use (focused, or the theme's open-search state). Always
 * tucked while a dropdown (bh-select) is open: the keyboard is up and space is scarce.
 */
(function () {
  const section = document.querySelector('.header-section');
  const form = section && section.querySelector('.searchform');
  if (!form) return;

  const mobile = window.matchMedia('(max-width: 1067px)');
  const TOP = 200;  // always shown above this, so the compensating margin is never on screen
  let last = window.scrollY;
  let tucked = false;
  let gap = null;

  function set(on) {
    if (on === tucked) return;
    if (on) {
      // The margin must equal exactly what the row gives back. Measure the CONTENT, never the
      // box: a quick down-up-down catches the row mid-transition, and a half-height reading then
      // became the untucked max-height too, leaving the search pill hanging out of the header's
      // background (2026-10-14). scrollHeight ignores max-height; the gap is read once at rest.
      if (gap === null) gap = getComputedStyle(form).marginTop;
      section.style.setProperty('--bh-search-h', `${form.scrollHeight}px`);
      section.style.setProperty('--bh-search-gap', gap);
    }
    tucked = on;
    section.classList.toggle('bh-search-tucked', on);
  }

  // dir: +1 scrolling down, -1 up, 0 = no scroll (a dropdown opened or closed).
  function update(dir) {
    const y = window.scrollY;
    const inUse = form.contains(document.activeElement) || document.body.classList.contains('open-search');
    // An open dropdown (bh-select, e.g. the neighbourhoods) has the keyboard up and needs every
    // pixel; scrolling up while picking in it must not bring the search back (2026-10-11).
    const dropdown = !!document.querySelector('bh-select[open]');
    if (!mobile.matches || inUse || y < TOP) set(false);
    else if (dropdown || dir > 0) set(true);
    else if (dir < 0) set(false);
  }

  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    update(y > last + 4 ? 1 : y < last - 8 ? -1 : 0);
    last = y;
  }, { passive: true });

  // bh-select toggles its `open` attribute; no scroll event comes with that.
  new MutationObserver(() => update(0)).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });

  mobile.addEventListener('change', () => set(false));

  // While search is open, anchor the dimming overlay to the header's REAL bottom (CSS var
  // --bh-search-overlay-top, see bh-header-search.css). The theme's --header-offset is only
  // written on scroll, and the iPhone keyboard moves the page without one, which left the overlay
  // ~35px too high over the pill (2026-10-17). Every event that can move the header re-measures:
  // the class flip, the keyboard (visualViewport), a scroll, and the row's own untuck animation.
  const root = document.documentElement;
  const header = section.querySelector('.header') || section;
  function placeOverlay() {
    if (document.body.classList.contains('open-search')) {
      root.style.setProperty('--bh-search-overlay-top', `${Math.round(header.getBoundingClientRect().bottom) - 1}px`);
    } else {
      root.style.removeProperty('--bh-search-overlay-top');
    }
  }
  new MutationObserver(() => { placeOverlay(); setTimeout(placeOverlay, 300); })
    .observe(document.body, { attributes: true, attributeFilter: ['class'] });
  form.addEventListener('transitionend', placeOverlay);
  window.addEventListener('scroll', placeOverlay, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', placeOverlay);
    window.visualViewport.addEventListener('scroll', placeOverlay);
  }
})();
